import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../Utils/Axios";

const DEFAULT_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Espanol" },
  { code: "af", label: "African" },
  { code: "it", label: "Italian" },
  { code: "de", label: "German" },
  { code: "fr", label: "French" },
  { code: "pt", label: "Portuguese" },
];

const uiTranslations = {
  en: {
    welcome: "Welcome Our Job Portal!",
    saveJobs: "Save Jobs",
    language: "Language",
    home: "Home",
    findJobs: "Find Jobs",
    aboutUs: "About Us",
    blog: "Blog",
    contact: "Contact",
    dashboard: "Dashboard",
    signIn: "Sign In",
    logout: "Logout",
  },
  es: {
    welcome: "Bienvenido a nuestro portal de empleo!",
    saveJobs: "Guardar empleos",
    language: "Idioma",
    home: "Inicio",
    findJobs: "Buscar empleos",
    aboutUs: "Sobre nosotros",
    blog: "Blog",
    contact: "Contacto",
    dashboard: "Panel",
    signIn: "Iniciar sesion",
    logout: "Cerrar sesion",
  },
  af: {
    welcome: "Welkom by ons werkportaal!",
    saveJobs: "Stoor poste",
    language: "Taal",
    home: "Tuis",
    findJobs: "Vind poste",
    aboutUs: "Oor ons",
    blog: "Blog",
    contact: "Kontak",
    dashboard: "Paneel",
    signIn: "Teken in",
    logout: "Teken uit",
  },
};

const LanguageContext = createContext(null);
const originalTextMap = new WeakMap();
const canonicalByText = (() => {
  const map = {};
  Object.entries(uiTranslations.en).forEach(([key, english]) => {
    map[String(english).trim()] = String(english).trim();
    Object.values(uiTranslations).forEach((langPack) => {
      const variant = String(langPack[key] || "").trim();
      if (variant) {
        map[variant] = String(english).trim();
      }
    });
  });
  map["Taal"] = "Language";
  map["Idioma"] = "Language";
  return map;
})();

const preserveSpacing = (source, translated) => {
  const leading = source.match(/^\s*/)?.[0] || "";
  const trailing = source.match(/\s*$/)?.[0] || "";
  return `${leading}${translated}${trailing}`;
};

const canTranslateNode = (node) => {
  if (!node?.parentElement) return false;
  const tag = node.parentElement.tagName;
  if (["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA"].includes(tag)) return false;
  if (node.parentElement.closest(".notranslate")) return false;
  const text = String(node.nodeValue || "").trim();
  if (!text) return false;
  return /[A-Za-z]/.test(text);
};

const fetchTranslation = async (text, lang, cacheRef) => {
  const source = String(text || "").trim();
  if (!source || lang === "en") return source;
  const cacheKey = `${lang}::${source}`;
  if (cacheRef.current.has(cacheKey)) return cacheRef.current.get(cacheKey);

  try {
    const response = await api.get("/api/settings/translate", {
      params: { q: source, tl: lang },
    });
    const translated = String(response.data?.translated || source);
    cacheRef.current.set(cacheKey, translated || source);
    return translated || source;
  } catch {
    cacheRef.current.set(cacheKey, source);
    return source;
  }
};

export const LanguageProvider = ({ children }) => {
  const [languages, setLanguages] = useState(DEFAULT_LANGUAGES);
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("site_language") || "en"
  );
  const isApplyingRef = useRef(false);
  const scheduledRef = useRef(null);
  const translationCacheRef = useRef(new Map());
  const observerRef = useRef(null);

  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const res = await api.get("/api/settings/languages");
        const remoteLanguages = Array.isArray(res.data?.languages) ? res.data.languages : [];
        const merged = [...remoteLanguages, ...DEFAULT_LANGUAGES].reduce((acc, item) => {
          if (!item?.code || !item?.label) return acc;
          if (!acc.some((lang) => lang.code === item.code)) {
            acc.push(item);
          }
          return acc;
        }, []);
        setLanguages(merged.length ? merged : DEFAULT_LANGUAGES);
      } catch {
        setLanguages(DEFAULT_LANGUAGES);
      }
    };
    loadLanguages();
  }, []);

  const applyLanguage = async () => {
    if (typeof document === "undefined") return;
    if (isApplyingRef.current) return;
    isApplyingRef.current = true;

    try {
      const lang = selectedLanguage || "en";
      localStorage.setItem("site_language", lang);
      document.documentElement.lang = lang;
      document.body.classList.remove(
        "lang-en",
        "lang-es",
        "lang-af",
        "lang-it",
        "lang-de",
        "lang-fr",
        "lang-pt"
      );
      document.body.classList.add(`lang-${lang}`);

      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (!canTranslateNode(node)) continue;
        textNodes.push(node);
        if (!originalTextMap.has(node)) {
          const current = String(node.nodeValue || "");
          const trimmed = current.trim();
          const canonical = canonicalByText[trimmed] || trimmed;
          originalTextMap.set(node, preserveSpacing(current, canonical));
        }
      }

      const placeholderFields = Array.from(
        document.querySelectorAll("input[placeholder], textarea[placeholder]")
      );
      placeholderFields.forEach((field) => {
        if (!field.hasAttribute("data-original-placeholder")) {
          const current = field.getAttribute("placeholder") || "";
          const trimmed = current.trim();
          const canonical = canonicalByText[trimmed] || trimmed;
          field.setAttribute("data-original-placeholder", preserveSpacing(current, canonical));
        }
      });

      const optionNodes = Array.from(document.querySelectorAll("option"));
      optionNodes.forEach((opt) => {
        if (!opt.hasAttribute("data-original-text")) {
          const current = opt.textContent || "";
          const trimmed = current.trim();
          const canonical = canonicalByText[trimmed] || trimmed;
          opt.setAttribute("data-original-text", preserveSpacing(current, canonical));
        }
      });

      if (lang === "en") {
        textNodes.forEach((node) => {
          const original = originalTextMap.get(node);
          if (typeof original === "string" && node.nodeValue !== original) {
            node.nodeValue = original;
          }
        });
        placeholderFields.forEach((field) => {
          const original = field.getAttribute("data-original-placeholder") || "";
          if (field.getAttribute("placeholder") !== original) {
            field.setAttribute("placeholder", original);
          }
        });
        optionNodes.forEach((opt) => {
          const original = opt.getAttribute("data-original-text") || "";
          if (opt.textContent !== original) {
            opt.textContent = original;
          }
        });
        return;
      }

      const uniqueTexts = new Set();
      textNodes.forEach((node) => {
        uniqueTexts.add((originalTextMap.get(node) || "").trim());
      });
      placeholderFields.forEach((field) => {
        uniqueTexts.add((field.getAttribute("data-original-placeholder") || "").trim());
      });
      optionNodes.forEach((opt) => {
        uniqueTexts.add((opt.getAttribute("data-original-text") || "").trim());
      });

      const dictionary = {};
      await Promise.all(
        Array.from(uniqueTexts)
          .filter(Boolean)
          .map(async (txt) => {
            dictionary[txt] = await fetchTranslation(txt, lang, translationCacheRef);
          })
      );

      textNodes.forEach((node) => {
        const original = originalTextMap.get(node) || "";
        const translated = dictionary[original.trim()] || original.trim();
        const nextValue = preserveSpacing(original, translated);
        if (node.nodeValue !== nextValue) {
          node.nodeValue = nextValue;
        }
      });

      placeholderFields.forEach((field) => {
        const original = field.getAttribute("data-original-placeholder") || "";
        const translated = dictionary[original.trim()] || original;
        const nextValue = preserveSpacing(original, translated);
        if (field.getAttribute("placeholder") !== nextValue) {
          field.setAttribute("placeholder", nextValue);
        }
      });

      optionNodes.forEach((opt) => {
        const original = opt.getAttribute("data-original-text") || "";
        const translated = dictionary[original.trim()] || original;
        if (opt.textContent !== translated) {
          opt.textContent = translated;
        }
      });
    } finally {
      isApplyingRef.current = false;
    }
  };

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const schedule = () => {
      if (scheduledRef.current) return;
      scheduledRef.current = window.setTimeout(async () => {
        scheduledRef.current = null;
        await applyLanguage();
      }, 200);
    };

    schedule();
    const t1 = window.setTimeout(schedule, 600);
    const t2 = window.setTimeout(schedule, 1200);

    const observer = new MutationObserver(() => {
      if (!isApplyingRef.current) schedule();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
      if (scheduledRef.current) {
        window.clearTimeout(scheduledRef.current);
        scheduledRef.current = null;
      }
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const t = (key) => {
    const active = uiTranslations[selectedLanguage] || uiTranslations.en;
    return active[key] || uiTranslations.en[key] || key;
  };

  const value = useMemo(
    () => ({
      languages,
      selectedLanguage,
      setSelectedLanguage,
      t,
    }),
    [languages, selectedLanguage]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
